import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, Check, X, RefreshCw, Play } from 'lucide-react';
import courtRecordsService from '../../services/courtRecordsService';
import caseService from '../../services/caseService';

// Admin → Court Records (feat/court-crawler): the D1 proposal table.
// Discovered portal cases surface with their evidence; a human confirms the
// matter mapping (audit-logged server-side); confirmed mappings are never
// re-derived; nothing crawls until confirmed. No fuzzy matching anywhere —
// the only auto-suggestion is an exact court-case-number equality.

const STATUS_STYLES = {
  unmatched: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  suggested: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  confirmed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  ignored: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const MatterPicker = ({ onPick, initialQuery }) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const data = await caseService.getCases({ search: query, limit: 10 });
      setResults(Array.isArray(data) ? data : (data.cases || []));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="mt-2 p-3 bg-virgo-navy-800/60 rounded-lg border border-virgo-teal-700/30">
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          placeholder="Search matters (name or number)…"
          className="flex-1 px-3 py-1.5 bg-virgo-navy-900 border border-virgo-teal-700/40 rounded text-sm text-white"
        />
        <button onClick={search} disabled={searching}
          className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 rounded text-sm text-white">
          {searching ? '…' : 'Search'}
        </button>
      </div>
      {results.map((c) => (
        <button key={c.id} onClick={() => onPick(c)}
          className="block w-full text-left mt-1 px-2 py-1 rounded hover:bg-virgo-teal-700/20 text-sm text-virgo-navy-100">
          <span className="font-mono text-cyan-300 mr-2">{c.display_number || c.custom_number || `#${c.case_number}`}</span>
          {c.case_name}
        </button>
      ))}
    </div>
  );
};

const CourtRecordsAdmin = () => {
  const [identifiers, setIdentifiers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [pickingFor, setPickingFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ids, rs] = await Promise.all([
        courtRecordsService.listIdentifiers(),
        courtRecordsService.listRuns(10),
      ]);
      setIdentifiers(ids);
      setRuns(rs);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load court records.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async (ident, matterId) => {
    setBusy(true);
    try {
      await courtRecordsService.confirmMapping(ident.id, matterId);
      setPickingFor(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Confirm failed.');
    } finally {
      setBusy(false);
    }
  };

  const ignore = async (ident) => {
    if (!window.confirm(`Ignore portal case ${ident.case_number}? It will never be crawled.`)) return;
    try {
      await courtRecordsService.ignoreIdentifier(ident.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ignore failed.');
    }
  };

  const trigger = async (downloadDocuments) => {
    setBusy(true);
    try {
      await courtRecordsService.triggerRun(downloadDocuments);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Trigger failed.');
    } finally {
      setBusy(false);
    }
  };

  const pending = identifiers.filter((i) => i.mapping_status === 'unmatched' || i.mapping_status === 'suggested');
  const settled = identifiers.filter((i) => i.mapping_status === 'confirmed' || i.mapping_status === 'ignored');

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Landmark className="w-6 h-6 text-virgo-gold-400" />
          <h2 className="text-xl font-semibold text-virgo-gold-400">Court Records (CCIS)</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-2 border border-virgo-teal-700/40 rounded-lg text-sm text-virgo-navy-100 hover:bg-virgo-teal-700/20">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => trigger(false)} disabled={busy}
            title="Docket diff only — writes nothing to the document store (D2 gate mode)"
            className="flex items-center gap-2 px-3 py-2 border border-cyan-500/40 rounded-lg text-sm text-cyan-300 hover:bg-cyan-500/10">
            <Play className="w-4 h-4" /> Docket-only run
          </button>
          <button onClick={() => trigger(true)} disabled={busy}
            className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm text-white">
            <Play className="w-4 h-4" /> Full run
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white mb-2">
        Proposals awaiting confirmation
        {pending.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-amber-500/20 rounded-full text-xs text-amber-300 border border-amber-500/30">
            {pending.length}
          </span>
        )}
      </h3>
      {pending.length === 0 && (
        <p className="text-sm text-virgo-navy-200 mb-4">
          Nothing awaiting confirmation. New cases discovered from My Cases
          land here after each crawl; nothing is crawled until you confirm its
          matter.
        </p>
      )}
      {pending.map((ident) => (
        <div key={ident.id} className="mb-3 p-3 rounded-lg border border-virgo-teal-700/30">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLES[ident.mapping_status]}`}>
              {ident.mapping_status}
            </span>
            <span className="font-mono text-white text-sm">{ident.case_number}</span>
            {ident.county && <span className="text-xs text-virgo-navy-200">{ident.county}</span>}
            <span className="text-sm text-virgo-navy-100 truncate max-w-md">{ident.case_title}</span>
          </div>
          {ident.suggestion_evidence && (
            <p className="text-xs text-virgo-navy-200 mt-1">
              Suggested: <span className="font-mono text-cyan-300">{ident.suggestion_evidence.matter_display_number}</span>
              {' '}({ident.suggestion_evidence.matter_case_name}) — exact court-case-number match
              on <span className="font-mono">{ident.suggestion_evidence.matter_court_case_number}</span>
            </p>
          )}
          <div className="flex gap-2 mt-2">
            {ident.suggested_matter_id && (
              <button onClick={() => confirm(ident, ident.suggested_matter_id)} disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded text-sm text-white">
                <Check className="w-4 h-4" /> Confirm suggestion
              </button>
            )}
            <button onClick={() => setPickingFor(pickingFor === ident.id ? null : ident.id)}
              className="px-3 py-1.5 border border-cyan-500/40 rounded text-sm text-cyan-300 hover:bg-cyan-500/10">
              Map to matter…
            </button>
            <button onClick={() => ignore(ident)}
              className="flex items-center gap-1 px-3 py-1.5 border border-slate-500/40 rounded text-sm text-slate-300 hover:bg-slate-500/10">
              <X className="w-4 h-4" /> Ignore
            </button>
          </div>
          {pickingFor === ident.id && (
            <MatterPicker initialQuery="" onPick={(c) => confirm(ident, c.id)} />
          )}
        </div>
      ))}

      <h3 className="text-lg font-semibold text-white mt-6 mb-2">Mapped & ignored</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-virgo-navy-200 border-b border-virgo-teal-700/30">
              <th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3">Portal case</th>
              <th className="py-1 pr-3">Title</th>
              <th className="py-1">Last synced</th>
            </tr>
          </thead>
          <tbody>
            {settled.map((i) => (
              <tr key={i.id} className="border-b border-virgo-teal-700/10 text-virgo-navy-100">
                <td className="py-1 pr-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_STYLES[i.mapping_status]}`}>
                    {i.mapping_status}
                  </span>
                </td>
                <td className="py-1 pr-3 font-mono">{i.case_number}</td>
                <td className="py-1 pr-3 truncate max-w-md">{i.case_title}</td>
                <td className="py-1 whitespace-nowrap">
                  {i.last_synced_at ? new Date(i.last_synced_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-lg font-semibold text-white mt-6 mb-2">Recent runs</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-virgo-navy-200 border-b border-virgo-teal-700/30">
              <th className="py-1 pr-3">Started</th>
              <th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3">Cases</th>
              <th className="py-1 pr-3">New entries</th>
              <th className="py-1 pr-3">New docs</th>
              <th className="py-1">Errors</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-virgo-teal-700/10 text-virgo-navy-100 align-top">
                <td className="py-1 pr-3 whitespace-nowrap">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
                <td className="py-1 pr-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs border ${
                    r.status === 'ok' ? STATUS_STYLES.confirmed
                      : r.status === 'running' ? STATUS_STYLES.suggested
                        : STATUS_STYLES.unmatched}`}>
                    {r.status}
                  </span>
                </td>
                <td className="py-1 pr-3">{r.cases_completed}/{r.cases_total}</td>
                <td className="py-1 pr-3">{r.entries_new}</td>
                <td className="py-1 pr-3">{r.docs_new}</td>
                <td className="py-1 text-xs whitespace-pre-wrap max-w-md">{r.error_text || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CourtRecordsAdmin;
